import { useState, useEffect } from "react";
import { Alert } from "react-native";
import {
  getPatientAudio,
  getPatientNotes,
  type TAudioMetadata,
  updatePatientNote,
  deletePatientNote,
  type TUpdateNoteRequest,
} from "../../../../../../services/api-documentation";
import { mixpanel } from "../../../../../../utils/mixpanel";
import { TSession } from "../../../../../../utils/types";
import { logUIError } from "../../../../../../services/api-misc";

// Types
interface VoiceNote {
  id: string;
  url?: string;
  audioId?: string;
  filename: string;
  transcription: string;
  note: string;
  note_json?: any;
  type: "consultation" | "phone_call" | "follow_up" | string;
  date: string;
  is_signed?: boolean;
  signed_at?: string | null;
  signed_pdf_url?: string | null;
}

interface Patient {
  id: string;
  name: string;
  patientRecordId?: string;
  consultationDate: string;
  clinicOSUUID?: string;
}

type UnprocessedAudioMetadata = TAudioMetadata;

interface UseVoiceNotesReturn {
  voiceNotes: VoiceNote[];
  unprocessedAudios: UnprocessedAudioMetadata[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchVoiceNotes: () => Promise<void>;
  updateNote: (
    noteId: string,
    updatedNote: string,
    note: VoiceNote,
  ) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
}

const VOICE_NOTES_FETCH_TIMEOUT_MS = 20000;

type VoiceNotesErrorType =
  | "network_error"
  | "http_error"
  | "auth_expired"
  | "timeout";

const getErrorName = (error: unknown): string => {
  if (error && typeof error === "object" && "name" in error) {
    const value = (error as { name?: unknown }).name;
    if (typeof value === "string") return value;
  }
  return "UnknownError";
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

const getErrorStack = (error: unknown): string | undefined => {
  if (error instanceof Error && error.stack) return error.stack;
  return undefined;
};

const extractHttpStatus = (message: string): number | null => {
  const statusMatch = message.match(/\b(4\d\d|5\d\d)\b/);
  if (!statusMatch) return null;
  const parsed = Number(statusMatch[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const classifyVoiceNotesError = (
  error: unknown,
  timedOut: boolean,
): { type: VoiceNotesErrorType; statusCode?: number | null } => {
  if (timedOut || getErrorName(error) === "AbortError") {
    return { type: "timeout", statusCode: null };
  }

  const message = getErrorMessage(error);
  const statusCode = extractHttpStatus(message);
  const lowered = message.toLowerCase();

  if (
    statusCode === 401 ||
    statusCode === 403 ||
    lowered.includes("token__expire_token") ||
    lowered.includes("unauthorized") ||
    lowered.includes("forbidden")
  ) {
    return { type: "auth_expired", statusCode };
  }

  if (lowered.includes("failed to fetch")) {
    return { type: "network_error", statusCode };
  }

  return { type: "http_error", statusCode };
};

type VoiceNotesEndpointError = {
  endpoint: "notes" | "audio";
  cause: unknown;
  elapsedMs: number;
};

export const useVoiceNotes = (
  patient: Patient,
  session: TSession | null,
  refreshKey: number = 0,
): UseVoiceNotesReturn => {
  const [voiceNotes, setVoiceNotes] = useState<VoiceNote[]>([]);
  const [unprocessedAudios, setUnprocessedAudios] = useState<
    UnprocessedAudioMetadata[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const logVoiceNotesUIError = async (
    message: string,
    extraContext?: Record<string, unknown>,
  ) => {
    try {
      await logUIError({
        message,
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || undefined,
        context: {
          component: "useVoiceNotes",
          patientId: patient?.clinicOSUUID,
          patientName: patient?.name,
          patientClinicOSUUID: patient?.clinicOSUUID,
          ...extraContext,
        },
      });
    } catch (err) {
      console.warn("logVoiceNotesUIError failed", err);
    }
  };

  useEffect(() => {
    if (patient?.clinicOSUUID) {
      fetchVoiceNotes();
    }
  }, [patient, refreshKey]);

  const fetchVoiceNotes = async (): Promise<void> => {
    // console.log(
    //   "[useVoiceNotes] fetchVoiceNotes called",
    //   {
    //     patientId: patient?.id,
    //     patientClinicOSUUID: patient?.clinicOSUUID,
    //     clinicId: session?.clinicId,
    //     hasAccessToken: !!session?.accessToken,
    //   },
    // );
    setLoading(true);
    setError(null);
    const fetchStartedAt = Date.now();
    let timedOut = false;

    try {
      if (!session || !session.clinicId || !patient.clinicOSUUID) {
        console.warn("[useVoiceNotes] fetchVoiceNotes skipped", {
          missingSession: !session,
          missingClinicId: !session?.clinicId,
          missingClinicOSUUID: !patient.clinicOSUUID,
        });
        setVoiceNotes([]);
        setUnprocessedAudios([]);
        setLoading(false);
        return;
      }
      const clinicId = session.clinicId;
      const patientClinicOSUUID = patient.clinicOSUUID;

      const abortController = new AbortController();
      const timeoutId = setTimeout(() => {
        timedOut = true;
        abortController.abort();
      }, VOICE_NOTES_FETCH_TIMEOUT_MS);

      const runWithEndpoint = async <T>(
        endpoint: "notes" | "audio",
        runner: () => Promise<T>,
      ): Promise<T> => {
        const startedAt = Date.now();
        try {
          return await runner();
        } catch (cause) {
          const endpointError: VoiceNotesEndpointError = {
            endpoint,
            cause,
            elapsedMs: Date.now() - startedAt,
          };
          throw endpointError;
        }
      };

      const [notesData, patientAudio] = await Promise.all([
        runWithEndpoint("notes", () =>
          getPatientNotes(clinicId, patientClinicOSUUID, {
            signal: abortController.signal,
          }),
        ),
        runWithEndpoint("audio", () =>
          getPatientAudio(session, clinicId, patientClinicOSUUID, false, {
            signal: abortController.signal,
          }),
        ),
      ]).finally(() => clearTimeout(timeoutId));

      // Filter out patient summary notes before processing
      const filteredNotesData = notesData.filter(
        (note) => note.note_type !== "patient_summary",
      );

      // Format notes for display and fetch transcripts
      const formattedNotesPromises = filteredNotesData.map(async (note) => {
        const transcriptUrl = note.audio_info?.[0]?.transcription_get_url || "";

        let transcription = "";
        if (transcriptUrl) {
          try {
            const response = await fetch(transcriptUrl);
            if (response.ok) {
              transcription = await response.text();
            }
          } catch (error) {
            console.error("Error fetching transcript:", error);
          }
        }

        const encounterTypeRaw =
          note.encounter_type ?? note.audio_info?.[0]?.encounter_type ?? null;
        const encounterType =
          typeof encounterTypeRaw === "string" &&
          encounterTypeRaw.trim().length > 0
            ? encounterTypeRaw.trim()
            : "Consultation";

        return {
          id: note.id.toString(),
          url: note.audio_info?.[0]?.get_url || "",
          audioId: note.audio_info?.[0]?.id.toString() || "",
          filename: `note-${note.id}`,
          transcription: transcription,
          note: note.note,
          note_json: note.note_json,
          type: encounterType,
          date: note.created_at,
          is_signed: note.is_signed,
          signed_at: note.signed_at,
          signed_pdf_url: note.signed_pdf_url,
        };
      });

      const formattedNotes = await Promise.all(formattedNotesPromises);
      const notesWithUrls = formattedNotes.filter((note) => note.url).length;
      const linkedAudioIds = new Set<number>();
      notesData.forEach((note) => {
        (note.audio_ids || []).forEach((audioId) => {
          const normalizedAudioId = Number(audioId);
          if (Number.isFinite(normalizedAudioId)) {
            linkedAudioIds.add(normalizedAudioId);
          }
        });
        (note.audio_info || []).forEach((audio) => {
          const normalizedAudioId = Number(audio.id);
          if (Number.isFinite(normalizedAudioId)) {
            linkedAudioIds.add(normalizedAudioId);
          }
        });
      });

      const unmatchedAudios = (patientAudio.metadata || [])
        .filter((audio) => !linkedAudioIds.has(audio.id))
        .sort(
          (left, right) =>
            new Date(right.created_at).getTime() -
            new Date(left.created_at).getTime(),
        );

      setVoiceNotes(formattedNotes);
      setUnprocessedAudios(unmatchedAudios);

      // Track success - update to show filtered count
      mixpanel.track("Voice Notes Fetch Completed", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Notes Count": formattedNotes.length,
        "Notes With Audio": notesWithUrls,
        "Total Audio Files": patientAudio.metadata?.length || 0,
        "Unprocessed Audio Count": unmatchedAudios.length,
        "Total Notes (including summaries)": notesData.length, // Optional: track total including filtered
        "Patient Summary Notes Filtered":
          notesData.length - filteredNotesData.length,
      });
    } catch (error) {
      setError("Failed to load voice notes");
      setVoiceNotes([]);
      setUnprocessedAudios([]);

      const endpointError = (error &&
        typeof error === "object" &&
        "endpoint" in error &&
        "cause" in error) as VoiceNotesEndpointError | false;
      const rootError = endpointError ? endpointError.cause : error;
      const classification = classifyVoiceNotesError(rootError, timedOut);
      const overallElapsedMs = Date.now() - fetchStartedAt;
      const isOnline =
        typeof navigator !== "undefined" ? navigator.onLine : undefined;

      // Track error
      mixpanel.track("Voice Notes Fetch Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Error Message":
          rootError instanceof Error ? rootError.message : "Unknown error",
        "Error Type": classification.type,
        "Error Status Code": classification.statusCode ?? undefined,
        Endpoint: endpointError ? endpointError.endpoint : "unknown",
        "Overall Elapsed Ms": overallElapsedMs,
        "Endpoint Elapsed Ms": endpointError
          ? endpointError.elapsedMs
          : undefined,
        "Network Online": isOnline,
      });

      logVoiceNotesUIError("Voice notes fetch failed", {
        errorType: classification.type,
        statusCode: classification.statusCode ?? undefined,
        endpoint: endpointError ? endpointError.endpoint : "unknown",
        timedOut,
        timeoutMs: VOICE_NOTES_FETCH_TIMEOUT_MS,
        endpointElapsedMs: endpointError ? endpointError.elapsedMs : undefined,
        overallElapsedMs,
        isOnline,
        errorName: getErrorName(rootError),
        error: getErrorMessage(rootError),
        stackTrace: getErrorStack(rootError),
      });
    } finally {
      setLoading(false);
    }
  };

  const updateNote = async (
    noteId: string,
    updatedNote: string,
    note: VoiceNote,
  ): Promise<void> => {
    if (!patient.clinicOSUUID || !session?.clinicId) {
      Alert.alert("Error", "Unable to save summary due to missing information");
      return;
    }

    setSaving(true);

    try {
      // Track save attempt
      mixpanel.track("Voice Note Summary Save Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": noteId,
      });

      const updatePayload: TUpdateNoteRequest = {
        note: updatedNote.trim(),
        note_json: note.note_json,
        audio_ids: note.audioId ? [Number(note.audioId)] : undefined,
      };

      await updatePatientNote(
        session,
        session.clinicId,
        patient.clinicOSUUID,
        Number(noteId),
        updatePayload,
      );

      // Update local state
      setVoiceNotes((prevNotes) =>
        prevNotes.map((n) =>
          n.id === noteId ? { ...n, note: updatedNote.trim() } : n,
        ),
      );

      // Track success
      mixpanel.track("Voice Note Summary Saved", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": noteId,
      });

      Alert.alert("Success", "Summary updated successfully");
    } catch (error) {
      console.error("Error updating summary:", error);

      // Track error
      mixpanel.track("Voice Note Summary Save Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": noteId,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Voice note summary save failed", {
        noteId,
        encounterType: note?.type,
        error: error instanceof Error ? error.message : String(error),
      });

      Alert.alert("Error", "Failed to update summary. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId: string): Promise<void> => {
    if (!patient.clinicOSUUID || !session?.clinicId) {
      Alert.alert("Error", "Missing information needed to delete note");
      return;
    }

    try {
      const noteToDelete = voiceNotes.find((note) => note.id === noteId);

      // Track delete attempt
      mixpanel.track("Voice Note Delete Started", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": noteId,
        "Note Type": noteToDelete?.type,
      });

      await deletePatientNote(
        session.clinicId,
        patient.clinicOSUUID,
        Number(noteId),
      );

      // Update local state
      setVoiceNotes((prevNotes) =>
        prevNotes.filter((note) => note.id !== noteId),
      );

      // Track success
      mixpanel.track("Voice Note Deleted", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": noteId,
        "Note Type": noteToDelete?.type,
      });

      Alert.alert("Success", "Note deleted successfully");
    } catch (error) {
      console.error("Error deleting note:", error);

      // Track error
      mixpanel.track("Voice Note Delete Error", {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": noteId,
        "Error Message":
          error instanceof Error ? error.message : "Unknown error",
      });

      logVoiceNotesUIError("Voice note delete failed", {
        noteId,
        error: error instanceof Error ? error.message : String(error),
      });

      Alert.alert("Error", "Failed to delete note. Please try again.");
    }
  };

  return {
    voiceNotes,
    unprocessedAudios,
    loading,
    saving,
    error,
    fetchVoiceNotes,
    updateNote,
    deleteNote,
  };
};
